// Translation Service
import { apiClient } from './api-client';

export interface Translation {
    id?: number;
    entity_type: 'form' | 'field' | 'section' | 'option' | 'row' | 'column';
    entity_id: number | string; // Support both number and string IDs
    field_name: string;
    language_code: string;
    translation: string;
    form_id?: number; // Form ID for filtering (required for saving)
    is_fallback?: boolean;
    created?: string;
    created_by?: number;
    modified?: string;
    modified_by?: number;
}

export interface TranslationStatus {
    id: number;
    form_id: number;
    language_code: string;
    total_fields: number;
    translated_fields: number;
    completion_percentage: number;
    is_complete: boolean;
    last_updated: string;
}

export interface Language {
    lang_id: number;
    lang_code: string;
    title: string;
    title_native: string;
    sef: string;
    image: string;
    description: string;
    published: number;
}

export interface FormTranslations {
    form: Translation[];
    fields: Translation[];
    sections: Translation[];
    options: Translation[];
    rows: Translation[];
    columns: Translation[];
}

export const translationsService = {
    /**
     * Get translations for a specific entity
     */
    async getEntityTranslations(
        entityType: string,
        entityId: number | string,
        languageCode?: string
    ): Promise<Translation[]> {
        const params: any = {
            entity_type: entityType,
            entity_id: entityId,
        };

        if (languageCode) {
            params.language_code = languageCode;
        }

        return apiClient.callProxy('translations', 'getEntityTranslations', params);
    },

    /**
     * Get all translations for a form (including fields and sections)
     */
    async getTranslations(
        formId: number,
        languageCode?: string
    ): Promise<FormTranslations> {
        const params: any = { form_id: formId };

        if (languageCode) {
            params.language_code = languageCode;
        }

        return apiClient.callProxy('translations', 'getTranslations', params);
    },

    /**
     * Save one or more translations
     */
    async saveTranslations(
        formId: number,
        translations: Omit<Translation, 'id' | 'created' | 'modified' | 'form_id'>[]
    ): Promise<{ success: boolean }> {
        return apiClient.callProxy(
            'translations',
            'saveTranslations',
            {
                form_id: formId,  // Send form_id as separate parameter for security
                translations: translations
            },
            'POST'
        );
    },

    /**
     * Save a single translation
     */
    async saveTranslation(
        formId: number,
        entityType: string,
        entityId: number | string,
        fieldName: string,
        languageCode: string,
        translation: string
    ): Promise<{ success: boolean }> {
        return this.saveTranslations(formId, [
            {
                entity_type: entityType as any,
                entity_id: entityId,
                field_name: fieldName,
                language_code: languageCode,
                translation,
            },
        ]);
    },

    /**
     * Delete translation(s)
     */
    async deleteTranslations(
        entityType: string,
        entityId: number | string,
        fieldName?: string,
        languageCode?: string
    ): Promise<{ success: boolean }> {
        const params: any = {
            entity_type: entityType,
            entity_id: entityId,
        };

        if (fieldName) {
            params.field_name = fieldName;
        }

        if (languageCode) {
            params.language_code = languageCode;
        }

        return apiClient.callProxy('translations', 'deleteTranslations', params, 'POST');
    },

    /**
     * Get available languages
     */
    async getLanguages(): Promise<Record<string, Language>> {
        return apiClient.callProxy('translations', 'getLanguages');
    },

    /**
     * Get available languages for frontend
     */
    async getFormLanguages(): Promise<Record<string, Language>> {
      return apiClient.callProxy('formsubmission', 'getFormLanguages');
    },

    /**
     * Get translation status for a form
     */
    async getTranslationStatus(formId: number): Promise<TranslationStatus[]> {
        const result = await apiClient.callProxy('translations', 'getTranslationStatus', {
            form_id: formId,
        });
        // Convert object to array if needed
        return Array.isArray(result) ? result : Object.values(result);
    },

    /**
     * Update form's multilingual settings
     */
    async updateFormLanguageSettings(
        formId: number,
        settings: {
            multilingual_enabled: boolean;
            default_language?: string;
            available_languages?: string[];
            language_detection?: 'browser' | 'user' | 'url' | 'manual';
            show_language_switcher?: boolean;
        }
    ): Promise<{ success: boolean }> {
        // This would be handled through the forms service
        // We'll add it to the forms.service.ts later
        return apiClient.callProxy('forms', 'saveForm', {
            data: {
                id: formId,
                ...settings,
            },
        });
    },
};

